import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/components/button';
import { Input } from '@/shared/ui/components/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/ui/components/form';
import {
  bootstrap,
  type BootstrapInput,
  SetupError,
  type SystemStatus,
  systemStatusQueryKey,
} from './api';

function makeSchema(t: ReturnType<typeof useTranslation>['t']) {
  return z
    .object({
      token: z.string().min(1, t('auth.setup.errors.tokenRequired')),
      email: z.email(t('auth.setup.errors.emailInvalid')),
      password: z.string().min(12, t('auth.setup.errors.passwordTooShort')),
      confirmPassword: z.string(),
    })
    .refine((v) => v.password === v.confirmPassword, {
      path: ['confirmPassword'],
      message: t('auth.setup.errors.passwordMismatch'),
    });
}

const SETUP_FIELDS = ['token', 'email', 'password'] as const;
type SetupField = (typeof SETUP_FIELDS)[number];

export function SetupWizard() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [alreadyConfigured, setAlreadyConfigured] = useState(false);

  const form = useForm<z.infer<ReturnType<typeof makeSchema>>>({
    resolver: zodResolver(makeSchema(t)),
    defaultValues: { token: '', email: '', password: '', confirmPassword: '' },
    mode: 'onSubmit',
  });

  const mutation = useMutation({
    mutationFn: (input: BootstrapInput) => bootstrap(input),
    onSuccess: () => {
      // Reflect the now-bootstrapped status so SystemStatusGate agrees on the
      // destination (it also redirects /setup -> /login once bootstrapped), then
      // refresh from the server and navigate.
      queryClient.setQueryData<SystemStatus>(systemStatusQueryKey, { bootstrapped: true });
      queryClient.invalidateQueries({ queryKey: systemStatusQueryKey });
      navigate('/login', { replace: true });
    },
    onError: (error) => {
      if (error instanceof SetupError) {
        if (error.status === 410) {
          setAlreadyConfigured(true);
          return;
        }
        if (error.field && (SETUP_FIELDS as readonly string[]).includes(error.field)) {
          form.setError(error.field as SetupField, { message: error.message });
        }
      }
    },
  });

  if (alreadyConfigured) {
    return (
      <section className="mx-auto grid max-w-sm gap-4 p-6 text-center">
        <h1 className="text-2xl font-semibold">{t('auth.setup.alreadyConfigured.title')}</h1>
        <p className="text-muted-foreground">{t('auth.setup.alreadyConfigured.body')}</p>
        <Link to="/login" className="text-primary underline">
          {t('auth.setup.alreadyConfigured.link')}
        </Link>
      </section>
    );
  }

  const submitError =
    !mutation.error || (mutation.error instanceof SetupError && mutation.error.field)
      ? null
      : t('auth.setup.errors.generic');

  return (
    <section className="mx-auto grid max-w-sm gap-6 p-6">
      <div className="grid gap-1">
        <h1 className="text-2xl font-semibold">{t('auth.setup.title')}</h1>
        <p className="text-muted-foreground text-sm">{t('auth.setup.subtitle')}</p>
      </div>
      <Form {...form}>
        <form
          className="grid gap-4"
          onSubmit={form.handleSubmit((values) =>
            mutation.mutate({
              token: values.token,
              email: values.email,
              password: values.password,
            }),
          )}
          noValidate
        >
          <FormField
            control={form.control}
            name="token"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.setup.token')}</FormLabel>
                <FormControl>
                  <Input autoComplete="off" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.setup.email')}</FormLabel>
                <FormControl>
                  <Input type="email" autoComplete="username" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.setup.password')}</FormLabel>
                <FormControl>
                  <Input type="password" autoComplete="new-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.setup.confirmPassword')}</FormLabel>
                <FormControl>
                  <Input type="password" autoComplete="new-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {submitError && (
            <p role="alert" className="text-destructive text-sm">
              {submitError}
            </p>
          )}
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? t('auth.setup.submitting') : t('auth.setup.submit')}
          </Button>
        </form>
      </Form>
    </section>
  );
}
